import type { PropsWithChildren, ReactNode } from 'react'

/**
 * Keeps project-card status notices readable inside the dashboard's bounded
 * grid columns without changing the shared AppCard contract.
 *
 * The generation-orphan notice is owned by AppCard, but its amber-on-amber
 * dark-mode treatment and non-wrapping action row do not fit the narrower
 * four-column Work grid. The dashboard reuses the shared warning surface
 * tokens here, preserves every word, and lets actions wrap instead of
 * widening or clipping the card.
 */
export function ProjectCardStatusFrame({
  children,
  relationship,
}: PropsWithChildren<{ relationship?: ReactNode }>) {
  return (
    <div
      className={[
        'min-w-0 max-w-full',
        '[&_[data-testid=app-card-generation-orphan]]:!border-warning/30',
        '[&_[data-testid=app-card-generation-orphan]]:!bg-warning/[0.08]',
        '[&_[data-testid=app-card-generation-orphan]_p]:!text-muted-foreground',
        '[&_[data-testid=app-card-generation-orphan]_p:first-child]:!text-foreground',
        '[&_[data-testid=app-card-generation-orphan]_p]:break-words',
        '[&_[data-testid=app-card-generation-orphan]>div]:flex-wrap',
        '[&_[data-testid=app-card-generation-orphan]_button]:max-w-full',
        '[&_[data-testid=app-card-generation-orphan]_button]:whitespace-normal',
        '[&_[data-testid=app-card-generation-orphan]_button]:break-words',
        '[&_[data-testid=app-card-generation-orphan]_button]:text-left',
        '[&_[data-testid=app-card-generation-orphan]_button]:leading-4',
      ].join(' ')}
    >
      {children}
      {relationship ? (
        <div
          data-testid="project-library-relationship"
          className="-mt-px flex flex-wrap items-center justify-between gap-2 rounded-b-xl border border-border bg-card px-3 py-2 text-[11px] text-muted-foreground"
        >
          {relationship}
        </div>
      ) : null}
    </div>
  )
}
